import { useEffect } from 'react';
import { captureRegion, useCamera, type CaptureRegion } from '../lib/scanner/useCamera.js';
import { useBarcodeScanner } from '../lib/scanner/useBarcodeScanner.js';
import { Button, Spinner, cx } from './ui.js';
import { IconCamera, IconCameraSwitch, IconTextScan } from './icons.js';

/**
 * A janela da camera — a mesma nas duas telas que leem alguma coisa.
 *
 * O componente e dono da camera e do leitor optico; a pagina so diz quando
 * ligar e recebe o que foi lido. Isso mantem os dois usos (QR de retirada e
 * etiqueta de entrada) com o mesmo comportamento de permissao, de troca de
 * camera e de erro — que e onde leitor de navegador costuma falhar de formas
 * diferentes em cada tela.
 */

interface CameraScannerProps {
  /** A pagina controla: fechar a camera ao sair do passo economiza bateria. */
  active: boolean;
  onDecode: (raw: string) => void;
  /** Sem isto o botao de ler texto nem aparece (a tela de QR nao precisa). */
  onCaptureText?: (canvas: HTMLCanvasElement) => void;
  captureLabel?: string;
  captureBusy?: boolean;
  /** Linha de instrucao sob a moldura. */
  hint?: string;
  className?: string;
}

/**
 * A moldura do texto: onde o porteiro encaixa o bloco do destinatario.
 *
 * Larga e baixa porque e essa a forma do bloco de endereco em etiqueta
 * brasileira — quatro ou cinco linhas curtas. Fica no terco superior porque o
 * codigo de barras costuma ocupar a metade de baixo, e recortar o barcode junto
 * so daria ao OCR uma mancha listrada para tentar interpretar como letra.
 */
const TEXT_REGION: CaptureRegion = { x: 0.06, y: 0.12, width: 0.88, height: 0.42 };

export function CameraScanner({
  active,
  onDecode,
  onCaptureText,
  captureLabel = 'Ler texto da etiqueta',
  captureBusy = false,
  hint,
  className,
}: CameraScannerProps) {
  const camera = useCamera();
  const live = camera.status === 'live';

  useBarcodeScanner({ video: camera.videoRef, active: live, onDecode });

  // Desestruturado: `camera` e um objeto novo a cada render, e usa-lo como
  // dependencia faria o efeito rodar em todo render. `start`/`stop` sao
  // estaveis.
  const { start, stop } = camera;
  useEffect(() => {
    if (active) start();
    else stop();
  }, [active, start, stop]);

  const failed =
    camera.status === 'denied' || camera.status === 'unsupported' || camera.status === 'error';

  return (
    <div className={className}>
      {/*
        No celular a janela NAO e 16:9.

        Com `aspect-video` fixo, um aparelho de 390px de largura dava uma tarja
        de ~220px de altura — e a moldura do OCR, que ocupa 42% dela, virava uma
        faixa de 90px. O porteiro nao tinha como encaixar o bloco do
        destinatario ali dentro, e o resultado parecia "o OCR nao presta"
        quando o problema era nunca ter cabido a etiqueta no recorte.

        Em retrato a janela passa a valer altura de tela de verdade (`svh`, e
        nao `vh`, porque a barra do navegador movel muda `vh` durante o scroll e
        a camera pularia junto). No desktop volta ao 16:9, que e a forma do
        webcam e nao tem por que crescer.

        Isto NAO desalinha o recorte: `captureRegion` converte a moldura da
        caixa visivel para pixel do frame ja levando o `object-cover` em conta,
        entao a regiao recortada acompanha a mudanca de proporcao.
      */}
      <div className="relative h-[60svh] max-h-[520px] overflow-hidden rounded-card border border-line bg-ink sm:aspect-video sm:h-auto sm:max-h-none">
        <video
          ref={camera.videoRef}
          playsInline
          muted
          // `object-cover` corta as bordas, mas o codigo fica no centro da mira
          // e a alternativa (letterbox) desperdicaria a area util no celular.
          className={cx(
            'size-full object-cover transition-opacity',
            live ? 'opacity-100' : 'opacity-0',
          )}
        />

        {/* Mira: cantos + linha de varredura */}
        {live && (
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <span className="absolute left-6 top-6 size-10 rounded-tl-lg border-l-2 border-t-2 border-accent" />
            <span className="absolute right-6 top-6 size-10 rounded-tr-lg border-r-2 border-t-2 border-accent" />
            <span className="absolute bottom-6 left-6 size-10 rounded-bl-lg border-b-2 border-l-2 border-accent" />
            <span className="absolute bottom-6 right-6 size-10 rounded-br-lg border-b-2 border-r-2 border-accent" />
            <span className="scanline absolute inset-x-10 top-1/2 h-0.5 rounded-full bg-accent" />
          </div>
        )}

        {/* Moldura do texto: so aparece quando a tela le etiqueta, e ela nao e
            enfeite — e exatamente o retangulo que vai ser recortado e enviado
            ao OCR. Sem uma marca visivel, o porteiro nao tem como saber que o
            enquadramento importa, e o recorte viraria loteria. */}
        {live && onCaptureText && (
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              className="absolute rounded-lg border-2 border-dashed border-warn/80"
              style={{
                left: `${TEXT_REGION.x * 100}%`,
                top: `${TEXT_REGION.y * 100}%`,
                width: `${TEXT_REGION.width * 100}%`,
                height: `${TEXT_REGION.height * 100}%`,
              }}
            >
              <span className="absolute -top-6 left-0 rounded-pill bg-warn/90 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-ink">
                Destinatario aqui
              </span>
            </div>
          </div>
        )}

        {/* Estados que ocupam a moldura inteira */}
        {!live && (
          <div className="absolute inset-0 grid place-items-center p-6 text-center">
            {camera.status === 'starting' ? (
              <span className="flex items-center gap-2 text-sm text-muted">
                <Spinner /> Abrindo a camera...
              </span>
            ) : failed ? (
              <div className="max-w-sm">
                <p className="text-sm font-semibold text-danger">{camera.message}</p>
                {camera.status === 'denied' && (
                  <Button variant="ghost" className="mt-4" onClick={camera.start}>
                    Tentar de novo
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <IconCamera className="mx-auto size-8 text-faint" />
                <p className="mt-3 text-sm text-muted">Camera desligada</p>
                <Button variant="ghost" className="mt-4" onClick={camera.start}>
                  Abrir camera
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Controles sobre o video. `pb-[max(0.75rem,env(safe-area-inset-bottom))]`
            tira o botao de cima da barra de gestos do iPhone, onde o toque as
            vezes vira "voltar" em vez de capturar. */}
        {live && (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-gradient-to-t from-ink/90 to-transparent p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={camera.flip}
              title="Trocar de camera"
              aria-label="Trocar de camera"
              className="rounded-full bg-ink/70 p-2.5 text-fg backdrop-blur transition hover:bg-ink"
            >
              <IconCameraSwitch className="size-5" />
            </button>

            {onCaptureText && (
              <Button
                onClick={() => {
                  const video = camera.videoRef.current;
                  if (!video) return;
                  // So o que esta dentro da moldura, na resolucao cheia. O
                  // frame inteiro fazia o Tesseract segmentar balcao e caixa
                  // junto com a etiqueta e quebrar o bloco do endereco.
                  const canvas = captureRegion(video, TEXT_REGION);
                  if (canvas) onCaptureText(canvas);
                }}
                loading={captureBusy}
                // `min-w-0` + `truncate`: em tela de 360px o rotulo longo
                // ("Lendo a etiqueta...") empurrava o botao de trocar camera
                // para fora da moldura.
                className="min-w-0 py-2.5"
              >
                {!captureBusy && <IconTextScan className="size-4 shrink-0" />}
                <span className="truncate">{captureLabel}</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {hint && <p className="mt-2 text-center text-xs text-faint">{hint}</p>}
    </div>
  );
}
